import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  useSelectCurrentApp,
  useSelectCurrentCompany,
  useSelectCurrentUser,
} from '@bbk/admin-runtime/active-context';
import { BlackToastTrigger } from '@bbkAdminComponents/alerts/black-toast';
import { appSlice } from '@bbkAdminRedux/app/app.slice';
import { type ActiveUser } from '@bbkAdminRedux/app/reducers';
import {
  selectApp,
  selectCompany,
  selectCompanyInternalName,
  selectUser,
} from '@bbkAdminRedux/app/selectors';
import { useAppDispatch } from '@bbkAdminRedux/redux_utils';
import { hasUsableAuthenticityToken } from '@bbkAdminRedux/rtkq/auth-status';
import { setAuthToken } from '@bbkAdminRedux/rtkq/auth-token.slice';
import { bbkApiSlice } from '@bbkAdminRedux/rtkq/bbkApi.slice';
import { preauthCurrentUserSlice } from '@bbkAdminRedux/rtkq/preauth-current-user.slice';
import {
  identifyWithAutopilot,
  identifyWithBeacon,
  identifyWithFullStory,
  identifyWithSegment,
  identifyWithSegmentGroup,
} from '@bbkAdminUtils/analytics';

export type {
  AppKey,
  CompanyId,
  CompanyKey,
  DateString,
} from '@bbkAdminRedux/app/reducers';

export type UserRolesResp = {
  collection: {
    description: string;
    name: string;
  }[];
};
export const currentUserSlice = bbkApiSlice.injectEndpoints({
  endpoints: (build) => ({
    getUserCompanyRoles: build.query<
      UserRolesResp['collection'],
      { companyKey: string }
    >({
      query: (args) => `/api/v1/${args.companyKey}/user_company_roles`,
      providesTags: ['UserCompanyRoles'],
      transformResponse: (res: UserRolesResp) => res.collection,
    }),
    identifyUser: build.mutation<unknown, unknown>({
      query: (body) => ({
        url: '/api/identify',
        method: 'POST',
        body,
      }),
    }),
  }),
});

export {
  useSelectCurrentUser,
  useSelectCurrentCompany,
  useSelectCompanyApps,
  useSelectCurrentApp,
} from '@bbk/admin-runtime/active-context';

export const useIdentifyUser = () => {
  const companyKey = useSelector(selectCompanyInternalName);

  const { isFetching: isUserFetching, isUninitialized: isUserUninitialized } =
    currentUserSlice.endpoints.getCurrentUser.useQuery();
  const user = useSelectCurrentUser();
  const company = useSelectCurrentCompany();
  const application = useSelectCurrentApp();
  const {
    data: roles,
    isFetching: isRolesFetching,
    isUninitialized: isRolesUninitialized,
  } = currentUserSlice.endpoints.getUserCompanyRoles.useQuery(
    { companyKey },
    { skip: companyKey === '_' }
  );
  const [identifyWithBrightback$] =
    currentUserSlice.endpoints.identifyUser.useMutation();

  const traits = useMemo(() => {
    if (isUserFetching || isRolesFetching) return undefined;

    if (
      !isUserUninitialized &&
      !isRolesUninitialized &&
      user &&
      company &&
      application &&
      roles
    ) {
      return {
        user,
        company,
        application,
        roles,
      };
    }
  }, [
    isUserFetching,
    isRolesFetching,
    isUserUninitialized,
    isRolesUninitialized,
    user,
    company,
    application,
    roles,
  ]);

  useEffect(() => {
    if (traits) {
      const { user, company, application, roles } = traits;
      const userId = user.id;
      const userTraits = {
        displayName: `${user.details.first_name} ${user.details.last_name}`,
        email: user.email,
        createdAt: user.created_at,
        roles, // passed in as part of the user object
      };
      const appTraits = {
        application: {
          id: application
            ? (application.internal_name ?? String(application.id))
            : undefined,
          name: application?.display_name,
        },
      };
      const companyTraits = {
        company: {
          name: company?.internal_name,
          id: company?.id,
          createdAt: company?.created_at,
        },
        company_type: company?.company_type,
        plan: company?.plan,
      };
      const identifyTraits = {
        ...userTraits,
        ...appTraits,
        ...companyTraits,
      };
      identifyWithFullStory(userId, { ...userTraits, ...companyTraits });
      identifyWithSegment(userId, identifyTraits);
      identifyWithBrightback$({
        user: identifyTraits,
        company: {
          internal_name: company.internal_name,
          name: company.name,
          company_type: company.company_type,
          domain: company.domain,
          companyId: company.id,
          active: company.active,
          plan: company.plan,
        },
      }).unwrap();
      identifyWithSegmentGroup(user, company);
      identifyWithBeacon(user, company);
      identifyWithAutopilot(user.email);
    }
  }, [identifyWithBrightback$, traits]);
};

export const selectCurrentUser = selectUser;

export const selectCurrentCompany = selectCompany;

export const selectCurrentApp = selectApp;
/**
 * Retry-fetch the current user until one with a usable authenticity token is
 * returned. Pure/testable core of {@link useSetBBAuthenticityToken}: it never
 * reads cached state, so a user retained from before a session change cannot
 * short-circuit the result. Returns `undefined` once retries are exhausted.
 */
export const resolveAuthenticatedUser = async (
  fetchUser: () => Promise<ActiveUser | undefined>,
  onAttemptError: () => void,
  maxRetries = 10
): Promise<ActiveUser | undefined> => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      const user = await fetchUser();
      if (hasUsableAuthenticityToken(user)) {
        return user;
      }
    } catch {
      onAttemptError();
    }
  }
  return undefined;
};

export const useSetBBAuthenticityToken = () => {
  const dispatch = useAppDispatch();
  const [validateCurrentUser$] =
    preauthCurrentUserSlice.endpoints.validateCurrentUser.useMutation();
  return useCallback(async () => {
    // Validate the new session through a non-caching request so we do NOT
    // populate the global getCurrentUser query mid-sign-in (that would flip
    // selectAuthStatus to authenticated on the sign-in page and mount the
    // logged-in tree before the trailing hard navigation). The resolved token
    // is held in memory so the company-creation POSTs can still send
    // BBK-Authenticity-Token; the reload then repopulates the query cache.
    const user = await resolveAuthenticatedUser(
      () => validateCurrentUser$().unwrap(),
      () => {
        dispatch(appSlice.actions.clearActiveContext());
      }
    );
    if (user) {
      dispatch(setAuthToken(user.authenticity_token));
    } else {
      BlackToastTrigger({
        content: 'Error logging in - unable to set authenticity token',
      });
    }
    return user;
  }, [dispatch, validateCurrentUser$]);
};
